import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import styles from '../capitalExpenditure.module.scss';
import {
  inferBudgetItemTypeFromCode,
  mergeBudgetItemsWithFixedTemplate,
  sortBudgetRowsByCode
} from '../../budget/budgetTemplate';
import { normalizeBudgetFiscalYearLabel } from '../../budget/fiscalYearUtils';

const formatBudgetValue = (value) => {
  const numericValue = Number(value || 0);
  if (!numericValue) {
    return '';
  }
  return numericValue.toLocaleString('en-IN');
};

const parseBudgetValue = (value) => {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatDateValue = (value) => {
  if (!value) {
    return 'N/A';
  }
  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return 'N/A';
  }
  return parsedDate.toLocaleDateString('ne-NP');
};

const toEditableRows = (items = [], fallbackItems = []) => {
  const mergedItems = mergeBudgetItemsWithFixedTemplate(items, fallbackItems);
  return mergedItems.map((item, index) => ({
    id: item?.id || index + 1,
    code_number: item?.code_number || '',
    expense_title: item?.expense_title || '',
    estimated_budget: item?.estimated_budget ?? '',
    remarks: item?.remarks || '',
    item_type: item?.item_type || inferBudgetItemTypeFromCode(item?.code_number, item?.item_type)
  }));
};

const getMetaFromRows = (rows = []) => {
  const capitalRows = sortBudgetRowsByCode(
    rows.filter((item) => inferBudgetItemTypeFromCode(item?.code_number, item?.item_type) === 'capital')
  );
  const operationalRows = sortBudgetRowsByCode(
    rows.filter((item) => inferBudgetItemTypeFromCode(item?.code_number, item?.item_type) === 'operational')
  );
  const capitalTotal = capitalRows.reduce((sum, item) => sum + parseBudgetValue(item?.estimated_budget), 0);
  const operationalTotal = operationalRows.reduce((sum, item) => sum + parseBudgetValue(item?.estimated_budget), 0);
  const grandTotal = capitalTotal + operationalTotal;

  return {
    capitalRows,
    operationalRows,
    rowCount: rows.length,
    capitalTotal,
    operationalTotal,
    grandTotal
  };
};

const statusLabel = (statusValue = '') => {
  if (statusValue === 'draft') {
    return 'मस्यौदा';
  }
  if (statusValue === 'submitted') {
    return 'पेश गरिएको';
  }
  if (statusValue === 'approved') {
    return 'मन्जुर';
  }
  if (statusValue === 'rejected') {
    return 'अस्वीकृत';
  }
  return 'पूर्ण';
};

const getBudgetRowKey = (row = {}, fallbackIndex = 0) => {
  const codeNumber = String(row?.code_number || '').trim();
  if (codeNumber) {
    return `code:${codeNumber}`;
  }
  const title = String(row?.expense_title || '').trim();
  if (title) {
    return `title:${title.toLowerCase()}`;
  }
  return `row:${fallbackIndex + 1}`;
};

const toDownloadValue = (value) => {
  const numericValue = parseBudgetValue(value);
  return numericValue ? numericValue : '';
};

const BudgetReport = ({
  loading,
  budgetItems,
  masterItems = [],
  filters,
  onViewBudget,
  onEditBudget,
  onDownloadBudget,
  onSaveBudget,
  onRefreshBudgetList,
  budgetActionLoadingId
}) => {
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupMode, setPopupMode] = useState('detail');
  const [popupLoading, setPopupLoading] = useState(false);
  const [popupSaving, setPopupSaving] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');
  const [popupMessageType, setPopupMessageType] = useState('info');
  const [popupSubmission, setPopupSubmission] = useState(null);
  const [popupFallbackItems, setPopupFallbackItems] = useState(masterItems);
  const [popupRows, setPopupRows] = useState([]);
  const [detailedReportPopupOpen, setDetailedReportPopupOpen] = useState(false);

  const hasDateFilter = Boolean(filters?.dateFrom || filters?.dateTo);
  const budgetSubmissions = useMemo(() => (
    Array.isArray(budgetItems) ? budgetItems : []
  ), [budgetItems]);

  const getSubmissionMeta = (submission, fallbackRows = masterItems) => {
    const items = Array.isArray(submission?.budget_items) ? submission.budget_items : [];
    return getMetaFromRows(toEditableRows(items, fallbackRows));
  };

  const popupMeta = useMemo(() => getMetaFromRows(popupRows), [popupRows]);
  const detailedBudgetReport = useMemo(() => {
    const submissions = Array.isArray(budgetSubmissions) ? budgetSubmissions : [];
    const institutions = [];
    const institutionSet = new Set();
    const rowMap = new Map();

    submissions.forEach((submission, submissionIndex) => {
      const institutionName = String(
        submission?.institution_name || `संस्था ${submissionIndex + 1}`
      ).trim() || `संस्था ${submissionIndex + 1}`;

      if (!institutionSet.has(institutionName)) {
        institutionSet.add(institutionName);
        institutions.push(institutionName);
      }

      const submissionRows = toEditableRows(submission?.budget_items || [], masterItems);
      submissionRows.forEach((row, rowIndex) => {
        const key = getBudgetRowKey(row, rowIndex);
        const rowType = inferBudgetItemTypeFromCode(row?.code_number, row?.item_type);

        if (!rowMap.has(key)) {
          rowMap.set(key, {
            key,
            code_number: row?.code_number || '',
            expense_title: row?.expense_title || '',
            item_type: rowType,
            values: {}
          });
        }

        const mappedRow = rowMap.get(key);
        if (!mappedRow.code_number && row?.code_number) {
          mappedRow.code_number = row.code_number;
        }
        if (!mappedRow.expense_title && row?.expense_title) {
          mappedRow.expense_title = row.expense_title;
        }
        mappedRow.item_type = inferBudgetItemTypeFromCode(mappedRow.code_number, mappedRow.item_type);
        mappedRow.values[institutionName] = (
          parseBudgetValue(mappedRow.values[institutionName]) + parseBudgetValue(row?.estimated_budget)
        );
      });
    });

    const allRows = sortBudgetRowsByCode(Array.from(rowMap.values())).map((row) => {
      const total = institutions.reduce(
        (sum, institutionName) => sum + parseBudgetValue(row?.values?.[institutionName]),
        0
      );
      return {
        ...row,
        total
      };
    });

    const capitalRows = allRows.filter(
      (row) => inferBudgetItemTypeFromCode(row?.code_number, row?.item_type) === 'capital'
    );
    const operationalRows = allRows.filter(
      (row) => inferBudgetItemTypeFromCode(row?.code_number, row?.item_type) === 'operational'
    );

    const buildInstitutionTotals = (rows = []) => institutions.reduce((acc, institutionName) => {
      acc[institutionName] = rows.reduce(
        (sum, row) => sum + parseBudgetValue(row?.values?.[institutionName]),
        0
      );
      return acc;
    }, {});

    const capitalTotalsByInstitution = buildInstitutionTotals(capitalRows);
    const operationalTotalsByInstitution = buildInstitutionTotals(operationalRows);
    const grandTotalsByInstitution = institutions.reduce((acc, institutionName) => {
      acc[institutionName] = (
        parseBudgetValue(capitalTotalsByInstitution[institutionName]) +
        parseBudgetValue(operationalTotalsByInstitution[institutionName])
      );
      return acc;
    }, {});

    const capitalTotal = capitalRows.reduce((sum, row) => sum + parseBudgetValue(row?.total), 0);
    const operationalTotal = operationalRows.reduce((sum, row) => sum + parseBudgetValue(row?.total), 0);
    const grandTotal = capitalTotal + operationalTotal;

    return {
      institutions,
      allRows,
      capitalRows,
      operationalRows,
      capitalTotalsByInstitution,
      operationalTotalsByInstitution,
      grandTotalsByInstitution,
      capitalTotal,
      operationalTotal,
      grandTotal
    };
  }, [budgetSubmissions, masterItems]);

  const detailedTableMinWidth = useMemo(() => (
    Math.max(980, ((detailedBudgetReport.institutions.length + 3) * 150))
  ), [detailedBudgetReport.institutions.length]);

  const closePopup = useCallback(() => {
    setPopupOpen(false);
    setPopupMode('detail');
    setPopupLoading(false);
    setPopupSaving(false);
    setPopupMessage('');
    setPopupMessageType('info');
    setPopupSubmission(null);
    setPopupRows([]);
  }, []);

  const closeDetailedReportPopup = useCallback(() => {
    setDetailedReportPopupOpen(false);
  }, []);

  useEffect(() => {
    if (!popupOpen && !detailedReportPopupOpen) {
      return undefined;
    }

    const handleEscClose = (event) => {
      if (event.key === 'Escape') {
        if (popupOpen) {
          closePopup();
        }
        if (detailedReportPopupOpen) {
          closeDetailedReportPopup();
        }
      }
    };

    window.addEventListener('keydown', handleEscClose);
    return () => {
      window.removeEventListener('keydown', handleEscClose);
    };
  }, [popupOpen, detailedReportPopupOpen, closePopup, closeDetailedReportPopup]);

  const handleDetailedReportExcelDownload = useCallback(() => {
    if (!detailedBudgetReport?.allRows?.length) {
      return;
    }

    const {
      institutions,
      capitalRows,
      operationalRows,
      capitalTotalsByInstitution,
      operationalTotalsByInstitution,
      grandTotalsByInstitution,
      capitalTotal,
      operationalTotal,
      grandTotal
    } = detailedBudgetReport;

    const excelData = [];
    excelData.push(['कोड नं.', 'खर्च शिर्षक', ...institutions, 'जम्मा']);
    excelData.push(['', 'पुँजीगत खर्च', ...institutions.map(() => ''), '']);

    capitalRows.forEach((row) => {
      excelData.push([
        row?.code_number || '',
        row?.expense_title || '',
        ...institutions.map((institutionName) => toDownloadValue(row?.values?.[institutionName])),
        toDownloadValue(row?.total)
      ]);
    });

    excelData.push([
      '',
      'जम्मा पुँजीगत',
      ...institutions.map((institutionName) => toDownloadValue(capitalTotalsByInstitution[institutionName])),
      toDownloadValue(capitalTotal)
    ]);

    excelData.push(['', 'संचालन खर्च', ...institutions.map(() => ''), '']);
    operationalRows.forEach((row) => {
      excelData.push([
        row?.code_number || '',
        row?.expense_title || '',
        ...institutions.map((institutionName) => toDownloadValue(row?.values?.[institutionName])),
        toDownloadValue(row?.total)
      ]);
    });

    excelData.push([
      '',
      'जम्मा संचालन',
      ...institutions.map((institutionName) => toDownloadValue(operationalTotalsByInstitution[institutionName])),
      toDownloadValue(operationalTotal)
    ]);

    excelData.push([
      '',
      'जम्मा संचालन र पुँजीगत खर्च',
      ...institutions.map((institutionName) => toDownloadValue(grandTotalsByInstitution[institutionName])),
      toDownloadValue(grandTotal)
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet(excelData);
    worksheet['!cols'] = [
      { wch: 14 },
      { wch: 42 },
      ...institutions.map(() => ({ wch: 22 })),
      { wch: 20 }
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'बजेट विस्तृत रिपोर्ट');

    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    XLSX.writeFile(workbook, `budget_detailed_report_${timestamp}.xlsx`);
  }, [detailedBudgetReport]);

  const openPopup = async (mode, submission) => {
    if (!submission?.id) {
      return;
    }

    setPopupOpen(true);
    setPopupMode(mode);
    setPopupLoading(true);
    setPopupMessage('');
    setPopupMessageType('info');

    let resolvedSubmission = submission;
    let resolvedFallbackItems = masterItems;

    try {
      const shouldFetch = mode === 'detail' || mode === 'view' || mode === 'edit';
      if (shouldFetch) {
        const fetcher = mode === 'edit' ? onEditBudget : onViewBudget;
        if (typeof fetcher === 'function') {
          const responseData = await fetcher(submission.id);
          if (responseData?.budget_submission) {
            resolvedSubmission = responseData.budget_submission;
            if (Array.isArray(responseData.fallback_items) && responseData.fallback_items.length > 0) {
              resolvedFallbackItems = responseData.fallback_items;
            }
          }
        }
      }

      const rows = toEditableRows(
        resolvedSubmission?.budget_items || [],
        resolvedFallbackItems
      );
      setPopupSubmission(resolvedSubmission);
      setPopupFallbackItems(resolvedFallbackItems);
      setPopupRows(rows);
    } catch (error) {
      console.error('Error opening budget popup:', error);
      setPopupMessage('बजेट डाटा लोड गर्न समस्या भयो');
      setPopupMessageType('error');
    } finally {
      setPopupLoading(false);
    }
  };

  const handlePopupInputChange = (rowId, field, value) => {
    setPopupRows((prevRows) => prevRows.map((row) => (
      row.id === rowId
        ? {
            ...row,
            [field]: value,
            ...(field === 'code_number' ? {
              item_type: inferBudgetItemTypeFromCode(value, row.item_type)
            } : {})
          }
        : row
    )));
  };

  const handlePopupSave = async () => {
    if (popupMode !== 'edit' || typeof onSaveBudget !== 'function' || !popupSubmission) {
      return;
    }

    try {
      setPopupSaving(true);
      setPopupMessage('');

      const savedSubmission = await onSaveBudget({
        submission: popupSubmission,
        rows: popupRows
      });

      if (!savedSubmission) {
        setPopupMessage('सेभ गर्न समस्या भयो');
        setPopupMessageType('error');
        return;
      }

      const refreshedRows = toEditableRows(
        savedSubmission?.budget_items || [],
        popupFallbackItems
      );
      setPopupSubmission(savedSubmission);
      setPopupRows(refreshedRows);
      setPopupMessage('बजेट डाटा सफलतापूर्वक सेभ भयो');
      setPopupMessageType('success');

      if (typeof onRefreshBudgetList === 'function') {
        await onRefreshBudgetList();
      }
    } catch (error) {
      console.error('Error saving budget popup data:', error);
      setPopupMessage('सेभ गर्न समस्या भयो');
      setPopupMessageType('error');
    } finally {
      setPopupSaving(false);
    }
  };

  const popupTitle = popupMode === 'edit'
    ? '✏️ डाटा सम्पादन गर्नुहोस्'
    : popupMode === 'view'
      ? '📊 पूर्ण डाटा हेर्नुहोस्'
      : 'विवरण हेर्नुहोस्';

  const isReadOnlyMode = popupMode !== 'edit';

  return (
    <div className={styles.detailedReportSection}>
      <div className={styles.detailedHeader} style={{ textAlign: 'left' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            flexWrap: 'wrap'
          }}
        >
          <div>
            <h3 style={{ margin: '0 0 6px 0' }}>बजेट</h3>
            <p style={{ margin: 0 }}>फर्म अनुसार सेभ भएको बजेट विवरण (View / Edit / Download)</p>
          </div>
          <div className={styles.detailedReportTabs} style={{ marginBottom: 0, padding: 0 }}>
            <button
              type="button"
              className={`${styles.detailedTabButton} ${styles.activeDetailedTab}`}
              onClick={() => setDetailedReportPopupOpen(true)}
              disabled={loading || detailedBudgetReport.allRows.length === 0}
              title="विस्तृत रिपोर्ट हेर्नुहोस्"
            >
              📋 विस्तृत रिपोर्ट
            </button>
            <button
              type="button"
              className={styles.detailedTabButton}
              onClick={handleDetailedReportExcelDownload}
              disabled={loading || detailedBudgetReport.allRows.length === 0}
              title="Excel मा डाउनलोड गर्नुहोस्"
            >
              📊 Excel डाउनलोड
            </button>
          </div>
        </div>
      </div>

      {hasDateFilter && (
        <div className={styles.filterInfo}>
          <p>
            फिल्टर मिति:
            {' '}
            {filters?.dateFrom || 'सुरु'}
            {' '}
            -
            {' '}
            {filters?.dateTo || 'हालसम्म'}
          </p>
        </div>
      )}

      <div className={styles.dataTableContainer}>
        {loading ? (
          <div className={styles.loading}>बजेट डाटा लोड हुँदैछ...</div>
        ) : budgetSubmissions.length === 0 ? (
          <div className={styles.noData}>
            <div className={styles.noDataContent}>
              <h3>कुनै बजेट डाटा फेला परेन</h3>
              <p>फिल्टर परिवर्तन गरेर पुन: प्रयास गर्नुहोस्।</p>
            </div>
          </div>
        ) : (
          <table className={styles.dataTable}>
            <thead>
              <tr>
                <th>संस्था</th>
                <th>आर्थिक वर्ष</th>
                <th>स्थिति</th>
                <th>पंक्ति संख्या</th>
                <th>जम्मा अनुमानित बजेट</th>
                <th>सिर्जना मिति</th>
                <th>अन्तिम अपडेट</th>
                <th>कार्यहरू</th>
              </tr>
            </thead>
            <tbody>
              {budgetSubmissions.map((submission) => {
                const submissionMeta = getSubmissionMeta(submission);
                const loadingCurrent = budgetActionLoadingId === submission.id;

                return (
                  <tr className={styles.dataRow} key={`budget-submission-${submission.id}`}>
                    <td>{submission.institution_name || 'N/A'}</td>
                    <td>{normalizeBudgetFiscalYearLabel(submission.fiscal_year_display || '') || 'N/A'}</td>
                    <td>
                      <span className={`${styles.statusBadge} ${styles[submission.status || 'draft']}`}>
                        {statusLabel(submission.status)}
                      </span>
                    </td>
                    <td>{submissionMeta.rowCount}</td>
                    <td>{formatBudgetValue(submissionMeta.grandTotal)}</td>
                    <td>{formatDateValue(submission.created_at)}</td>
                    <td>{formatDateValue(submission.updated_at)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
                        <button
                          type="button"
                          className={styles.viewButton}
                          onClick={() => openPopup('detail', submission)}
                          disabled={loadingCurrent}
                        >
                          विवरण हेर्नुहोस्
                        </button>
                        <button
                          type="button"
                          className={styles.viewButton}
                          disabled={loadingCurrent}
                          onClick={() => openPopup('view', submission)}
                        >
                          📊 पूर्ण डाटा हेर्नुहोस्
                        </button>
                        <button
                          type="button"
                          className={styles.editButton}
                          disabled={loadingCurrent}
                          onClick={() => openPopup('edit', submission)}
                        >
                          ✏️ डाटा सम्पादन गर्नुहोस्
                        </button>
                        {typeof onDownloadBudget === 'function' && (
                          <button
                            type="button"
                            className={styles.viewButton}
                            disabled={loadingCurrent}
                            onClick={() => onDownloadBudget(submission.id)}
                          >
                            ⬇️ डाउनलोड गर्नुहोस्
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {detailedReportPopupOpen && (
        <div className={styles.budgetActionOverlay} onClick={closeDetailedReportPopup}>
          <div
            className={styles.budgetActionModal}
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className={styles.budgetActionHeader}>
              <h3>📋 विस्तृत रिपोर्ट</h3>
              <button
                type="button"
                className={styles.budgetActionClose}
                onClick={closeDetailedReportPopup}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className={styles.budgetActionBody}>
              {detailedBudgetReport.allRows.length === 0 ? (
                <div className={styles.noData}>
                  <div className={styles.noDataContent}>
                    <h3>कुनै बजेट डाटा फेला परेन</h3>
                    <p>विस्तृत रिपोर्ट हेर्न सेभ भएको बजेट आवश्यक छ।</p>
                  </div>
                </div>
              ) : (
                <div className={styles.summaryExcelTableContainer} style={{ marginBottom: 0 }}>
                  <div className={styles.tableWrapper}>
                    <table className={styles.summaryExcelTable} style={{ minWidth: `${detailedTableMinWidth}px` }}>
                      <thead>
                        <tr>
                          <th className={styles.budgetCodeHeader}>कोड नं.</th>
                          <th className={styles.budgetTitleHeader}>खर्च शिर्षक</th>
                          {detailedBudgetReport.institutions.map((institutionName) => (
                            <th key={`budget-report-header-${institutionName}`} className={styles.institutionHeader}>
                              {institutionName}
                            </th>
                          ))}
                          <th className={styles.totalHeader}>जम्मा</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className={styles.sectionHeaderRow}>
                          <td colSpan={detailedBudgetReport.institutions.length + 3} className={styles.sectionHeader}>
                            पुँजीगत खर्च
                          </td>
                        </tr>
                        {detailedBudgetReport.capitalRows.map((row) => (
                          <tr key={`budget-detailed-capital-${row.key}`} className={styles.dataRow}>
                            <td className={styles.budgetCode}>{row.code_number || ''}</td>
                            <td className={styles.budgetTitle}>{row.expense_title || ''}</td>
                            {detailedBudgetReport.institutions.map((institutionName) => (
                              <td key={`budget-detailed-capital-${row.key}-${institutionName}`} className={styles.institutionValue}>
                                {formatBudgetValue(row?.values?.[institutionName])}
                              </td>
                            ))}
                            <td className={styles.totalValue}>{formatBudgetValue(row.total)}</td>
                          </tr>
                        ))}
                        <tr className={styles.subtotalRow}>
                          <td className={styles.budgetCode}></td>
                          <td className={styles.budgetTitle}><strong>जम्मा पुँजीगत</strong></td>
                          {detailedBudgetReport.institutions.map((institutionName) => (
                            <td key={`budget-detailed-capital-total-${institutionName}`} className={styles.institutionValue}>
                              <strong>{formatBudgetValue(detailedBudgetReport.capitalTotalsByInstitution[institutionName])}</strong>
                            </td>
                          ))}
                          <td className={styles.totalValue}>
                            <strong>{formatBudgetValue(detailedBudgetReport.capitalTotal)}</strong>
                          </td>
                        </tr>

                        <tr className={styles.sectionHeaderRow}>
                          <td colSpan={detailedBudgetReport.institutions.length + 3} className={styles.sectionHeader}>
                            संचालन खर्च
                          </td>
                        </tr>
                        {detailedBudgetReport.operationalRows.map((row) => (
                          <tr key={`budget-detailed-operational-${row.key}`} className={styles.dataRow}>
                            <td className={styles.budgetCode}>{row.code_number || ''}</td>
                            <td className={styles.budgetTitle}>{row.expense_title || ''}</td>
                            {detailedBudgetReport.institutions.map((institutionName) => (
                              <td key={`budget-detailed-operational-${row.key}-${institutionName}`} className={styles.institutionValue}>
                                {formatBudgetValue(row?.values?.[institutionName])}
                              </td>
                            ))}
                            <td className={styles.totalValue}>{formatBudgetValue(row.total)}</td>
                          </tr>
                        ))}
                        <tr className={styles.subtotalRow}>
                          <td className={styles.budgetCode}></td>
                          <td className={styles.budgetTitle}><strong>जम्मा संचालन</strong></td>
                          {detailedBudgetReport.institutions.map((institutionName) => (
                            <td key={`budget-detailed-operational-total-${institutionName}`} className={styles.institutionValue}>
                              <strong>{formatBudgetValue(detailedBudgetReport.operationalTotalsByInstitution[institutionName])}</strong>
                            </td>
                          ))}
                          <td className={styles.totalValue}>
                            <strong>{formatBudgetValue(detailedBudgetReport.operationalTotal)}</strong>
                          </td>
                        </tr>

                        <tr className={styles.grandTotalRow}>
                          <td className={styles.budgetCode}></td>
                          <td className={styles.budgetTitle}><strong>जम्मा संचालन र पुँजीगत खर्च</strong></td>
                          {detailedBudgetReport.institutions.map((institutionName) => (
                            <td key={`budget-detailed-grand-total-${institutionName}`} className={styles.institutionValue}>
                              <strong>{formatBudgetValue(detailedBudgetReport.grandTotalsByInstitution[institutionName])}</strong>
                            </td>
                          ))}
                          <td className={styles.totalValue}>
                            <strong>{formatBudgetValue(detailedBudgetReport.grandTotal)}</strong>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <div className={styles.budgetActionActions}>
              <button
                type="button"
                className={styles.budgetActionButton}
                onClick={closeDetailedReportPopup}
              >
                बन्द गर्नुहोस्
              </button>
              <button
                type="button"
                className={`${styles.budgetActionButton} ${styles.budgetActionSave}`}
                onClick={handleDetailedReportExcelDownload}
                disabled={detailedBudgetReport.allRows.length === 0}
              >
                📊 Excel डाउनलोड
              </button>
            </div>
          </div>
        </div>
      )}

      {popupOpen && (
        <div className={styles.budgetActionOverlay} onClick={closePopup}>
          <div
            className={styles.budgetActionModal}
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className={styles.budgetActionHeader}>
              <h3>{popupTitle}</h3>
              <button
                type="button"
                className={styles.budgetActionClose}
                onClick={closePopup}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className={styles.budgetActionMeta}>
              <span><strong>संस्था:</strong> {popupSubmission?.institution_name || 'N/A'}</span>
              <span><strong>आर्थिक वर्ष:</strong> {normalizeBudgetFiscalYearLabel(popupSubmission?.fiscal_year_display || '') || 'N/A'}</span>
              <span><strong>स्थिति:</strong> {statusLabel(popupSubmission?.status || 'draft')}</span>
            </div>

            <div className={styles.budgetActionBody}>
              {popupLoading ? (
                <div className={styles.loading}>डाटा लोड हुँदैछ...</div>
              ) : (
                <div className={styles.summaryExcelTableContainer} style={{ marginBottom: 0 }}>
                  <div className={styles.tableWrapper}>
                    <table className={styles.summaryExcelTable} style={{ minWidth: '720px' }}>
                      <thead>
                        <tr>
                          <th className={styles.budgetCodeHeader}>कोड नं.</th>
                          <th className={styles.budgetTitleHeader}>खर्च शिर्षक</th>
                          <th className={styles.totalHeader}>अनुमानित बजेट</th>
                          <th className={styles.totalHeader}>कैफियत</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className={styles.sectionHeaderRow}>
                          <td colSpan="4" className={styles.sectionHeader}>पुँजीगत खर्च</td>
                        </tr>
                        {popupMeta.capitalRows.map((item) => (
                          <tr key={`budget-popup-capital-${item.id}`} className={styles.dataRow}>
                            <td className={styles.budgetCode}>
                              {isReadOnlyMode ? (
                                item.code_number || ''
                              ) : (
                                <input
                                  type="text"
                                  value={item.code_number}
                                  onChange={(e) => handlePopupInputChange(item.id, 'code_number', e.target.value)}
                                  className={styles.budgetPopupInput}
                                />
                              )}
                            </td>
                            <td className={styles.budgetTitle}>
                              {isReadOnlyMode ? (
                                item.expense_title || ''
                              ) : (
                                <input
                                  type="text"
                                  value={item.expense_title}
                                  onChange={(e) => handlePopupInputChange(item.id, 'expense_title', e.target.value)}
                                  className={styles.budgetPopupInput}
                                />
                              )}
                            </td>
                            <td className={styles.totalValue}>
                              {isReadOnlyMode ? (
                                formatBudgetValue(item.estimated_budget)
                              ) : (
                                <input
                                  type="number"
                                  value={item.estimated_budget}
                                  onChange={(e) => handlePopupInputChange(item.id, 'estimated_budget', e.target.value)}
                                  className={styles.budgetPopupInput}
                                />
                              )}
                            </td>
                            <td className={styles.institutionValue}>
                              {isReadOnlyMode ? (
                                item.remarks || ''
                              ) : (
                                <input
                                  type="text"
                                  value={item.remarks}
                                  onChange={(e) => handlePopupInputChange(item.id, 'remarks', e.target.value)}
                                  className={styles.budgetPopupInput}
                                />
                              )}
                            </td>
                          </tr>
                        ))}
                        <tr className={styles.subtotalRow}>
                          <td className={styles.budgetCode}></td>
                          <td className={styles.budgetTitle}><strong>जम्मा पुँजीगत</strong></td>
                          <td className={styles.totalValue}><strong>{formatBudgetValue(popupMeta.capitalTotal)}</strong></td>
                          <td className={styles.institutionValue}></td>
                        </tr>

                        <tr className={styles.sectionHeaderRow}>
                          <td colSpan="4" className={styles.sectionHeader}>संचालन खर्च</td>
                        </tr>
                        {popupMeta.operationalRows.map((item) => (
                          <tr key={`budget-popup-operational-${item.id}`} className={styles.dataRow}>
                            <td className={styles.budgetCode}>
                              {isReadOnlyMode ? (
                                item.code_number || ''
                              ) : (
                                <input
                                  type="text"
                                  value={item.code_number}
                                  onChange={(e) => handlePopupInputChange(item.id, 'code_number', e.target.value)}
                                  className={styles.budgetPopupInput}
                                />
                              )}
                            </td>
                            <td className={styles.budgetTitle}>
                              {isReadOnlyMode ? (
                                item.expense_title || ''
                              ) : (
                                <input
                                  type="text"
                                  value={item.expense_title}
                                  onChange={(e) => handlePopupInputChange(item.id, 'expense_title', e.target.value)}
                                  className={styles.budgetPopupInput}
                                />
                              )}
                            </td>
                            <td className={styles.totalValue}>
                              {isReadOnlyMode ? (
                                formatBudgetValue(item.estimated_budget)
                              ) : (
                                <input
                                  type="number"
                                  value={item.estimated_budget}
                                  onChange={(e) => handlePopupInputChange(item.id, 'estimated_budget', e.target.value)}
                                  className={styles.budgetPopupInput}
                                />
                              )}
                            </td>
                            <td className={styles.institutionValue}>
                              {isReadOnlyMode ? (
                                item.remarks || ''
                              ) : (
                                <input
                                  type="text"
                                  value={item.remarks}
                                  onChange={(e) => handlePopupInputChange(item.id, 'remarks', e.target.value)}
                                  className={styles.budgetPopupInput}
                                />
                              )}
                            </td>
                          </tr>
                        ))}
                        <tr className={styles.subtotalRow}>
                          <td className={styles.budgetCode}></td>
                          <td className={styles.budgetTitle}><strong>जम्मा संचालन</strong></td>
                          <td className={styles.totalValue}><strong>{formatBudgetValue(popupMeta.operationalTotal)}</strong></td>
                          <td className={styles.institutionValue}></td>
                        </tr>
                        <tr className={styles.grandTotalRow}>
                          <td className={styles.budgetCode}></td>
                          <td className={styles.budgetTitle}><strong>जम्मा संचालन र पुँजीगत खर्च</strong></td>
                          <td className={styles.totalValue}><strong>{formatBudgetValue(popupMeta.grandTotal)}</strong></td>
                          <td className={styles.institutionValue}></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {popupMessage && (
              <div className={`${styles.budgetActionInfo} ${styles[popupMessageType]}`}>
                {popupMessage}
              </div>
            )}

            <div className={styles.budgetActionActions}>
              <button
                type="button"
                className={styles.budgetActionButton}
                onClick={closePopup}
              >
                बन्द गर्नुहोस्
              </button>
              {popupMode === 'edit' && (
                <button
                  type="button"
                  className={`${styles.budgetActionButton} ${styles.budgetActionSave}`}
                  onClick={handlePopupSave}
                  disabled={popupSaving || popupLoading}
                >
                  {popupSaving ? 'सेभ हुँदैछ...' : 'सेभ गर्नुहोस्'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BudgetReport;
